import React, { createContext, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import useAuth from "hooks/useAuth";
import {
  createOrganiseListApi,
  fetchOrganiseList,
  fetchOrganiseLogsApi,
  fetchRecentlyModifedOrganiseList,
  removeOrganiseListApi,
  removeUserFromOrganiseList,
  shareOrganiseListApi,
} from "api/organiseList";
import useProperties from "hooks/useProperties";

export const OrganiseListContext = createContext();

export const useOrganiseList = () => useContext(OrganiseListContext);

export const OrganiseListProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [isFetchingList, setIsFetchingList] = useState(false);
  const [isFetchingOrganiseLogs, setIsFetchingOrganiseLogs] = useState(false);
  const [organiseLogs, setOrganiseLogs] = useState([]);
  const [organiseLists, setOrganiseLists] = useState([]);
  const [recetnlyModifiedOrganiseLists, setRecentlyModifiedOrganiseLists] =
    useState([]);
  const [selectedOrganiseList, setSelectedOrganiseList] = useState({});
  const [sharedWithLoading, setSharedWithLoading] = useState(false);
  const [isDoubtPropertiesActive, setIsDoubtPropertiesActive] = useState(false);
  const [isDiscardedPropertiesActive, setIsDiscardedPropertiesActive] =
    useState(false);
  const { user } = useAuth();

  // Fetch organise lists
  const loadLists = async () => {
    const authUser = JSON.parse(localStorage.getItem("user"));
    if (!authUser && !authUser?.token) return;

    setIsFetchingList(true);
    try {
      const lists = await fetchOrganiseList();
      setOrganiseLists(lists);
    } catch (error) {
      // toast.error(error.message);
    } finally {
      setIsFetchingList(false);
    }
  };

  useEffect(() => {
    loadLists();
  }, [user]);

  // Get selected list from local storage
  useEffect(() => {
    if (organiseLists.length > 0) {
      const savedSelection =
        JSON.parse(localStorage.getItem("origaniseSelectionHistory")) || [];

      // Sort customOrganiseLists based on savedSelection
      const sortedLists = organiseLists.sort((a, b) => {
        const indexA = savedSelection.indexOf(a._id);
        const indexB = savedSelection.indexOf(b._id);
        return (
          (indexA === -1 ? Infinity : indexA) -
          (indexB === -1 ? Infinity : indexB)
        );
      });

      const foundList =
        savedSelection.length > 0 ? sortedLists[0] : organiseLists[0];

      setSelectedOrganiseList(foundList);
    }
  }, [organiseLists]);

  // Create organise list
  const createOrganiseList = async (list) => {
    if (!user) return;

    setLoading(true);
    try {
      const newList = await createOrganiseListApi(list);
      setOrganiseLists((prev) => [...prev, newList.organiseList]);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Select origanise list and save in localstorage
  const handleSelectOrganiseList = (list) => {
    setSelectedOrganiseList(list);

    // Current selection history
    const currentSelection =
      JSON.parse(localStorage.getItem("origaniseSelectionHistory")) || [];

    // Remove already exists list
    const updatedSelection = currentSelection.filter((_id) => _id !== list._id);

    // Add new selection to the front positon
    updatedSelection.unshift(list._id);

    // Save local storage
    localStorage.setItem(
      "origaniseSelectionHistory",
      JSON.stringify(updatedSelection)
    );
  };

  // Share organise list with other users
  const shareOrganiseList = async (email) => {
    setSharedWithLoading(true);
    try {
      const response = await shareOrganiseListApi(
        selectedOrganiseList?._id,
        email,
        user?.firstName,
        user?.lastName
      );
      await loadLists();
      toast.success(response.message);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSharedWithLoading(false);
    }
  };

  // Remove user from shared list
  const removeUserFromSharedList = async (id) => {
    setSharedWithLoading(true);
    try {
      await removeUserFromOrganiseList(selectedOrganiseList?._id, id);

      setOrganiseLists((prevLists) =>
        prevLists.map((list) => {
          if (list._id === selectedOrganiseList._id) {
            // Filter out the removed user from the sharedWith array
            return {
              ...list,
              sharedWith: list.sharedWith.filter((user) => user._id !== id),
            };
          }
          return list;
        })
      );
    } catch (err) {
      toast.error(err.message || "An error occurred");
    } finally {
      setSharedWithLoading(false);
    }
  };

  // Remove organise list
  const removeOrganiseList = async (id) => {
    setLoading(true);
    try {
      await removeOrganiseListApi(id);
      await loadLists();
    } catch (err) {
      toast.error(err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  // Fetch organsie edit logs
  const fetchOrganiseLog = async (nextUrl = null) => {
    setIsFetchingOrganiseLogs(true);
    try {
      const logs = await fetchOrganiseLogsApi(
        selectedOrganiseList._id,
        nextUrl
      );
      if (nextUrl) {
        setOrganiseLogs((prevVal) => ({
          ...logs,
          results: [...prevVal.results, ...logs.results],
        }));
      } else {
        setOrganiseLogs(logs);
      }
    } catch (error) {
      console.error(error.message);
    } finally {
      setIsFetchingOrganiseLogs(false);
    }
  };

  const clearOrganiseList = () => {
    setOrganiseLists([]);
    setOrganiseLogs([]);
    setSelectedOrganiseList({});
  };

  // Fetch recently modified organise list
  const fetchRecentlyModifiedOrganiseList = async (nextUrl = null) => {
    setIsFetchingList(true);
    try {
      const results = await fetchRecentlyModifedOrganiseList(
        selectedOrganiseList._id,
        nextUrl
      );

      setRecentlyModifiedOrganiseLists(results);
    } catch (error) {
      console.error(error.message);
    } finally {
      setIsFetchingList(false);
    }
  };

  return (
    <OrganiseListContext.Provider
      value={{
        organiseLists,
        selectedOrganiseList,
        createOrganiseList,
        handleSelectOrganiseList,
        shareOrganiseList,
        removeUserFromSharedList,
        sharedWithLoading,
        removeOrganiseList,
        loading,
        isFetchingList,
        isFetchingOrganiseLogs,
        organiseLogs,
        fetchOrganiseLog,
        clearOrganiseList,
        isDoubtPropertiesActive,
        isDiscardedPropertiesActive,
        setIsDoubtPropertiesActive,
        setIsDiscardedPropertiesActive,
        fetchRecentlyModifiedOrganiseList,
        recetnlyModifiedOrganiseLists,
      }}
    >
      {children}
    </OrganiseListContext.Provider>
  );
};
